/**
 * Retries a whole async operation (not partial/internal progress) a capped number of times,
 * waiting between attempts. Extracted from PdfViewer's load path so the retry contract itself
 * is unit-testable independent of pdf.js.
 */

export interface RetryAsyncOptions {
  /** Total attempts, including the first -- not a retry count. */
  attempts: number;
  /** Delay before each retry, or a function of the attempt number just failed (1-based). */
  delayMs: number | ((failedAttempt: number) => number);
  /** Polled before each attempt (including the first) and before each delay; stops early once true. */
  isCancelled?: () => boolean;
}

export async function retryAsync<T>(
  operation: () => Promise<T>,
  options: RetryAsyncOptions
): Promise<T> {
  const { attempts, delayMs, isCancelled } = options;
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (isCancelled?.()) {
      // No attempt has run yet in this branch, so lastError may still be unset -- fall back to a
      // real Error rather than rejecting with undefined.
      throw (
        lastError ?? new Error("retryAsync: cancelled before any attempt ran")
      );
    }
    try {
      return await operation();
    } catch (e) {
      lastError = e;
    }
    if (attempt === attempts || isCancelled?.()) {
      throw lastError;
    }
    const delay = typeof delayMs === "function" ? delayMs(attempt) : delayMs;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  throw lastError;
}
